import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { HeatmapAnswer, HeatmapDevice, HeatmapStat } from './heatmapInsights'
import type { HeatmapCaptureLayer } from './HeatmapCapture'

export type HeatmapAnswerCta = { label: string; href: string; onClick?: () => void }

interface HeatmapViewProps {
  topBar: ReactNode
  answer: HeatmapAnswer
  /** Summary numbers under the answer (volume, top element, reach). */
  stats?: HeatmapStat[]
  /** Secondary CTA beside the answer. Hidden while an element is selected. */
  cta?: HeatmapAnswerCta | null
  /** Replaces the CTA slot (the collecting state's progress). */
  answerAside?: ReactNode
  /** Notice under the answer (plan limit, stale capture). */
  notice?: ReactNode
  layer: HeatmapCaptureLayer
  device: HeatmapDevice
  /** "Captured Sep 15 · desktop, 1440px wide", with the capture picker and Recapture. */
  captureMeta: ReactNode
  /** Dots layer: "Showing each of the 21 clicks as a dot. …" */
  dotsCaption?: string
  intensity: number
  onIntensityChange: (value: number) => void
  capture: ReactNode
  /** Ranked element list beside the capture (clicks heat only). */
  aside?: ReactNode
}

/**
 * Heatmaps page body (Paper "Heatmaps · Simple · …"): the slim top bar, one
 * answer sentence with its subline and a secondary CTA, then the grey canvas
 * with the capture strip (meta, legend, intensity) above the page capture.
 */
export function HeatmapView({
  topBar,
  answer,
  stats,
  cta,
  answerAside,
  notice,
  layer,
  device,
  captureMeta,
  dotsCaption,
  intensity,
  onIntensityChange,
  capture,
  aside,
}: HeatmapViewProps) {
  const unit = device === 'mobile' ? 'taps' : 'clicks'
  return (
    <div className="flex min-h-full flex-1 flex-col">
      {topBar}
      <div
        className={cn(
          'flex flex-col gap-4 px-4 pb-6 pt-7 sm:flex-row sm:gap-6 sm:px-8',
          answerAside ? 'sm:items-center' : 'sm:items-end',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-ui-title">{answer.title}</h1>
          <p className="text-[13px] leading-4 text-muted-foreground">{answer.subline}</p>
        </div>
        {answerAside ??
          (cta && (
            <Button asChild variant="outline" className="h-8 shrink-0 self-start px-3 text-[13px] text-ui-title sm:self-auto">
              <Link to={cta.href} onClick={cta.onClick}>
                {cta.label}
              </Link>
            </Button>
          ))}
      </div>
      {stats && stats.length > 0 && (
        <dl className="mx-4 mb-5 grid grid-cols-2 overflow-hidden rounded-xl border border-border sm:mx-8 lg:grid-cols-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="flex min-w-0 flex-col gap-1 border-border px-4 py-3 [&:not(:first-child)]:border-l max-lg:[&:nth-child(3)]:border-l-0 max-lg:[&:nth-child(n+3)]:border-t"
            >
              <dt className="text-[12px] leading-4 text-muted-foreground">{stat.label}</dt>
              <dd className="text-[22px] font-semibold leading-7 tracking-[-0.02em] tabular-nums text-ui-title">{stat.value}</dd>
              <dd className="truncate text-[12px] leading-4 text-muted-foreground" title={stat.hint}>
                {stat.hint}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {notice && <div className="px-4 pb-4 sm:px-8">{notice}</div>}
      <div className="flex flex-1 flex-col items-center gap-3 border-t border-black/[0.04] bg-canvas px-4 pb-10 pt-5 sm:px-8 dark:border-white/[0.06]">
        <div className={cn('flex w-full flex-wrap items-center gap-x-2.5 gap-y-2', aside ? 'max-w-[1124px]' : 'max-w-[800px]')}>
          {layer === 'dots' ? (
            <div className="flex items-center gap-2">
              <span className="size-2 shrink-0 rounded-full bg-foreground shadow-[0_0_0_1.5px_#fff,0_0_0_2.5px_rgb(0_0_0/0.15)]" />
              <span className="text-[12px] leading-4 text-muted-foreground">{dotsCaption}</span>
            </div>
          ) : (
            <>
              {captureMeta}
              <div className="flex-1" />
              {layer === 'clicks' ? (
                <>
                  <LegendScale left={`Fewer ${unit}`} right={`More ${unit}`} className="bg-gradient-to-r from-primary-tint-ring via-chart-1 to-chart-2" />
                  <div className="flex items-center gap-2 sm:border-l sm:border-black/[0.06] sm:pl-3 sm:dark:border-white/10">
                    <span className="text-[12px] leading-4 text-foreground/60">Intensity</span>
                    <IntensitySlider value={intensity} onChange={onIntensityChange} />
                  </div>
                </>
              ) : (
                <LegendScale
                  left="Reached by all"
                  right="Reached by few"
                  className="bg-gradient-to-r from-card to-foreground/20"
                />
              )}
            </>
          )}
        </div>
        {aside ? (
          <div className="flex w-full max-w-[1124px] flex-col items-center gap-4 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-1 justify-center">{capture}</div>
            <div className="w-full lg:w-[300px] lg:shrink-0">{aside}</div>
          </div>
        ) : (
          capture
        )}
      </div>
    </div>
  )
}

function LegendScale({ left, right, className }: { left: string; right: string; className: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-[12px] leading-4 text-foreground/60">{left}</span>
      <span aria-hidden className={cn('h-1.5 w-[72px] shrink-0 rounded-[3px] shadow-[0_0_0_1px_rgb(0_0_0/0.07)]', className)} />
      <span className="text-[12px] leading-4 text-foreground/60">{right}</span>
    </div>
  )
}

/** Paper intensity control: 72px ink range on a hairline track, white 16px thumb. */
function IntensitySlider({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <SliderPrimitive.Root
      value={[Math.round(value * 100)]}
      onValueChange={([v]) => onChange(v / 100)}
      min={10}
      max={100}
      step={5}
      aria-label="Heat intensity"
      className="relative flex h-4 w-[72px] touch-none select-none items-center"
    >
      <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-[2px] bg-neutral-200 dark:bg-white/15">
        <SliderPrimitive.Range className="absolute h-full rounded-[2px] bg-foreground" />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb className="block size-4 rounded-full bg-white shadow-[0_0_0_1px_rgb(0_0_0/0.2),0_1px_2px_rgb(0_0_0/0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
    </SliderPrimitive.Root>
  )
}
